import React from 'react';
import { useTranslation } from 'react-i18next';
import avonovaLogo from 'assets/images/avologo.png';
import './ErrorFallback.css';

const SUPPORT_EMAIL = 'info@avonova.se';

type ErrorFallbackProps = {
  error?: Error;
  eventId?: string;
};

function clearLocalDataAndRestart() {
  localStorage.clear();
  sessionStorage.clear();

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => Promise.all(registrations.map((r) => r.unregister())))
      .catch(() => undefined)
      .finally(() => window.location.assign('/'));
    return;
  }

  window.location.assign('/');
}

const ErrorFallback = ({ error, eventId }: ErrorFallbackProps) => {
  const { t } = useTranslation('translation');

  return (
    <div className="error-page">
      <div className="error-page__card" role="alert">
        <img src={avonovaLogo} alt="Avonova" className="error-page__logo" />

        <h1 className="error-page__title">
          {t('errorPage.TITLE', { defaultValue: 'Something went wrong' })}
        </h1>

        <p className="error-page__body">
          {t('errorPage.BODY', {
            defaultValue:
              'The page could not be displayed because of an unexpected error. The error has been reported to us automatically.',
          })}
        </p>

        <div className="error-page__actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            {t('errorPage.RELOAD', { defaultValue: 'Reload page' })}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => window.location.assign('/')}
          >
            {t('errorPage.GO_TO_START', { defaultValue: 'Go to start page' })}
          </button>
        </div>

        <p className="error-page__support">
          {t('errorPage.SUPPORT', {
            defaultValue: 'If the problem keeps happening, please contact Avonova at',
          })}{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>

        {eventId && (
          <p className="error-page__reference">
            {t('errorPage.REFERENCE', { defaultValue: 'Reference' })}: <code>{eventId}</code>
          </p>
        )}

        <details className="error-page__details">
          <summary>
            {t('errorPage.TECHNICAL_DETAILS', { defaultValue: 'Technical details' })}
          </summary>
          <pre>{error?.message || String(error)}</pre>
        </details>

        <div className="error-page__last-resort">
          <button type="button" className="error-page__clear" onClick={clearLocalDataAndRestart}>
            {t('errorPage.CLEAR_DATA', { defaultValue: 'Sign out and clear local data' })}
          </button>
          <span className="error-page__clear-hint">
            {t('errorPage.CLEAR_DATA_HINT', {
              defaultValue: 'Last resort — you will need to log in again.',
            })}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
